<script setup lang="ts">
import { type BackendErrorEntity, type ErrorType, useApiCompleteOnboardingSession } from '~/apiClient';
import type { AuthGatedAction } from '~/components/Auth/AuthGatedActionInfo.vue';

interface Props {
    action?: AuthGatedAction;
}

// PROPS
const { action } = defineProps<Props>();

// EVENTS
const emit = defineEmits(['success']);

// COMPOSABLES
const { logError } = useLogs();
const { tt } = useTypedI18n();
const { notifyBackendError, notifyGenericError } = useNotification();
const { data: session, getSession } = useAuthUtils();
const { canSkipOnboarding } = useOnboarding();
const {
    mutateAsync: mutateCompleteOnboarding,
    isPending: isPendingCompleteOnboarding,
    isError: isErrorCompleteOnboarding,
} = useApiCompleteOnboardingSession();

const successLabel = computed(() => {
    switch (action) {
        case 'ADP_BID':
        case 'MAKE_OFFER':
            return tt('login.gatedActions.makeAnOffer');

        default:
            return tt('signup.onboarding.complete.successCta');
    }
});

// HANDLERS
const onCompleteOnboarding = async (): Promise<void> => {
    await mutateCompleteOnboarding()
        .catch((apiError: ErrorType<BackendErrorEntity>) => {
            logError('Error during signup onboarding thank you step: automatic complete onboarding', {
                apiError,
                userId: session.value?.user?.id,
            });

            if (!apiError.data?.error) {
                notifyGenericError();

                return;
            }

            const { error } = apiError.data;
            notifyBackendError({ error });
        })
        .then(() => getSession());
};

const onThankYouCta = (): void => {
    emit('success');
};

// LIFECYCLE
onMounted(() => {
    if (canSkipOnboarding.value) onCompleteOnboarding();
});
</script>

<template>
    <div class="flex flex-col gap-4">
        <template v-if="false && isErrorCompleteOnboarding">
            <p class="text-center">{{ tt('signup.onboarding.complete.errorSubtitle') }}</p>
            <FleequidCustomerServiceContact />
            <UButton
                :label="tt('signup.onboarding.complete.errorCta')"
                size="xl"
                block
                :loading="isPendingCompleteOnboarding"
                @click="onCompleteOnboarding"
            />
        </template>
        <template v-else>
            <p class="text-center">{{ tt('signup.onboarding.complete.successSubtitle') }}</p>
            <UButton :label="successLabel" size="xl" block :loading="isPendingCompleteOnboarding" @click="onThankYouCta" />
        </template>
    </div>
</template>
